import type { PreisToolItem } from '@/api/preis-tool/preis-tool.types';
import { DEFAULT_LOW_MARGIN_PCT, fmtLimitPct, marginClass } from './helpers';

/**
 * States of a row in the article list (EOS-61). They drive the colour legend
 * (filter buttons with counts), the row colour and the deal dot. Pure and
 * computed in the browser from the loaded row, i.e. from the shown prices (a
 * pending change wins over the SAP price) — unsaved drafts do not count, so
 * a filtered list does not shift while prices are being typed.
 */
export type RowState = 'noPrices' | 'partialPrices' | 'deal' | 'lowMargin' | 'underMek';

/** The limits the states depend on; shares (0.1 = 10 %). */
export interface LegendLimits {
  dealMinSavingsPct: number;
  lowMarginPct: number;
}

/** In effect while nothing is stored or the stored limits are still loading. */
export const DEFAULT_LEGEND_LIMITS: LegendLimits = {
  dealMinSavingsPct: 0.1,
  lowMarginPct: DEFAULT_LOW_MARGIN_PCT,
};

/** A row with several states takes the colour of the first one (see `rowColourState`). */
export const ROW_STATE_PRIORITY: readonly RowState[] = [
  'underMek',
  'lowMargin',
  'deal',
  'partialPrices',
  'noPrices',
];

/** Order of the legend buttons: price coverage first, then the margin states. */
export const LEGEND_ORDER: readonly RowState[] = [
  'noPrices',
  'partialPrices',
  'deal',
  'lowMargin',
  'underMek',
];

/** CSS class shared by the legend swatch and the row, so both use one colour. */
export const ROW_STATE_CLASS: Record<RowState, string> = {
  noPrices: 'pt-state-no-prices',
  partialPrices: 'pt-state-partial-prices',
  deal: 'pt-state-deal',
  lowMargin: 'pt-state-low-margin',
  underMek: 'pt-state-under-mek',
};

export const ROW_STATE_LABEL: Record<RowState, string> = {
  noPrices: 'Keine Preise',
  partialPrices: 'Teilweise',
  deal: 'Deal',
  lowMargin: 'Niedrige Marge',
  underMek: 'Unter MEK',
};

/** Tooltip of a legend button: what the state means with the limits in effect. */
export function rowStateDescription(state: RowState, limits: LegendLimits): string {
  switch (state) {
    case 'noPrices':
      return 'Keiner der drei Verkaufspreise (Standard, ITscope, Onliner) ist gepflegt.';
    case 'partialPrices':
      return 'Einer oder zwei der drei Verkaufspreise fehlen.';
    case 'deal':
      return `Bestes Lieferantenangebot mindestens ${fmtLimitPct(limits.dealMinSavingsPct)} unter dem MEK.`;
    case 'lowMargin':
      return `Mindestens ein Verkaufspreis mit einem Aufschlag von 0 bis unter ${fmtLimitPct(limits.lowMarginPct)} auf den MEK.`;
    case 'underMek':
      return 'Mindestens ein Verkaufspreis liegt unter dem MEK (Aufschlag unter 0 %).';
  }
}

/**
 * The backend compares the deal saving in exact decimals; this absorbs the
 * floating point error so an offer exactly at the limit counts here too.
 */
const DEAL_TOLERANCE = 1e-9;

type RowInput = Pick<
  PreisToolItem,
  'standardPrice' | 'itscopePrice' | 'onlinerPrice' | 'movingAveragePrice' | 'bestOffer'
>;

/**
 * Every state of one row, highest priority first (`[0]` is the row colour).
 * A row can be in several states at once:
 *
 * - keine Preise: none of the three shown sale prices is set (0 counts as not set)
 * - teilweise: one or two are missing
 * - unter MEK: MEK > 0 and at least one set price with a markup below 0
 * - niedrige Marge: MEK > 0 and at least one set price with 0 ≤ markup < lowMarginPct
 * - Deal: MEK > 0 and (MEK − best offer) / MEK ≥ dealMinSavingsPct
 *
 * Markup = (price − MEK) / MEK, as in the price cells.
 */
export function rowStates(item: RowInput, limits: LegendLimits): RowState[] {
  const found = new Set<RowState>();
  const prices = [item.standardPrice, item.itscopePrice, item.onlinerPrice];
  const set = prices.filter((p): p is number => !!p);
  if (set.length === 0) found.add('noPrices');
  else if (set.length < prices.length) found.add('partialPrices');

  const mek = item.movingAveragePrice;
  if (mek > 0) {
    for (const price of set) {
      const cls = marginClass((price - mek) / mek, limits.lowMarginPct);
      if (cls === 'neg') found.add('underMek');
      else if (cls === 'warn') found.add('lowMargin');
    }
    if (
      item.bestOffer &&
      (mek - item.bestOffer.netPrice) / mek >= limits.dealMinSavingsPct - DEAL_TOLERANCE
    ) {
      found.add('deal');
    }
  }
  return ROW_STATE_PRIORITY.filter((s) => found.has(s));
}

/**
 * The state that colours a row: the first of its states (highest priority)
 * among the active legend filters, so a row shown for "Deal" is green even
 * when it is under the MEK too. Without an active filter, the first state.
 */
export function rowColourState(
  states: readonly RowState[] | undefined,
  selected: readonly RowState[],
): RowState | undefined {
  if (!states) return undefined;
  if (selected.length === 0) return states[0];
  return states.find((s) => selected.includes(s));
}

/** States of every row by item id. */
export function rowStatesById(items: readonly PreisToolItem[], limits: LegendLimits) {
  return new Map(items.map((item) => [item.id, rowStates(item, limits)]));
}

/** How many of `rows` are in each state (a row counts once per state it has). */
export function countRowStates(
  rows: readonly PreisToolItem[],
  states: ReadonlyMap<string, readonly RowState[]>,
): Record<RowState, number> {
  const counts: Record<RowState, number> = {
    noPrices: 0,
    partialPrices: 0,
    deal: 0,
    lowMargin: 0,
    underMek: 0,
  };
  for (const row of rows) {
    for (const state of states.get(row.id) ?? []) counts[state]++;
  }
  return counts;
}

/** The legend filter: no selection keeps every row, else rows in any selected state. */
export function filterByRowStates(
  rows: PreisToolItem[],
  states: ReadonlyMap<string, readonly RowState[]>,
  selected: readonly RowState[],
): PreisToolItem[] {
  if (selected.length === 0) return rows;
  return rows.filter((row) => (states.get(row.id) ?? []).some((s) => selected.includes(s)));
}

/** Keeps only known states, e.g. from page state written by an older version. */
export function sanitizeRowStates(value: unknown): RowState[] {
  if (!Array.isArray(value)) return [];
  return LEGEND_ORDER.filter((s) => value.includes(s));
}
